/**
 * «Что изменит Telegram» — docs/phase-1/05-ui.md §4.3.
 *
 * Показывает `canonicalization_diff` модели рендера: разницу между черновиком как он пришёл
 * и его канонической формой (\r\n → \n, TAB → пробел, срезанные пустые строки, слитые и
 * разрезанные сущности, выброшенные пустые).
 *
 * Сегодня, при канонизации в Б3.4, список пуст всегда — и ровно поэтому блок ценен: любое
 * его непустое состояние означает, что что-то в конвейере сломалось. В фазе 2, когда
 * появится редактор, он станет главным местом, где человек видит «вы поставили таб — в
 * канале будет пробел».
 */

import type { JSX } from 'react';
import type { CanonicalizationDiffEntry } from '../api/types.ts';
import { IconChevronRight, IconWarn } from '../components/icons.tsx';

/**
 * Снятая карточка (`link_preview_dropped_for_rich`) — не «что изменит Telegram», а следствие
 * способа отправки, и полоса карточки превью уже называет его причину (картинка, файл, длина,
 * политика бренда). При политике «все посты — расширенным» это предупреждение стояло бы жёлтым
 * под каждым постом бренда, а блок, чьё непустое состояние значит «в конвейере что-то
 * сломалось», горел бы всегда (снимок 19.09.2026).
 */
const SHOWN_BY_CARD_STRIP: ReadonlySet<string> = new Set(['link_preview_dropped_for_rich']);

export function TelegramDiff({
  entries: all,
}: {
  readonly entries: readonly CanonicalizationDiffEntry[];
}): JSX.Element {
  const entries = all.filter((e) => !SHOWN_BY_CARD_STRIP.has(e.code));
  if (entries.length === 0) {
    return (
      <div className="micro" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <IconChevronRight />
        <span>Что изменит Telegram — ничего.</span>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="eyebrow">Что изменит Telegram</span>
      {entries.map((e) => (
        <div className="val val-warn" key={`${e.code}|${e.message}`}>
          <IconWarn />
          <span>
            {e.message} <span className="meta">{e.code}</span>
          </span>
        </div>
      ))}
    </div>
  );
}
